import type { TenantUser } from "@repo/shared/types";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { apiErrorCode, apiErrorMessage } from "@/lib/api/_client";
import { useActiveTenantUsers } from "@/lib/hooks/use-clients";
import { useReassignClients } from "@/lib/hooks/use-teams";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./reassign-clients-dialog.i18n";

interface ReassignClientsDialogProps {
  user: TenantUser | null;
  onClose: () => void;
}

export function ReassignClientsDialog({ user, onClose }: ReassignClientsDialogProps) {
  const t = useT(T);
  const reassign = useReassignClients();
  const { tenantUsers } = useActiveTenantUsers();
  const [toUserId, setToUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const isPending = reassign.isPending;

  const name = user?.name || user?.email || "";
  const options = tenantUsers
    .filter((u) => u.id !== user?.id)
    .map((u) => ({ value: u.id, label: u.name || t.unnamedUser }));

  const handleClose = () => {
    setToUserId("");
    setError(null);
    onClose();
  };

  const handleMove = async () => {
    if (!user || !toUserId) return;
    setError(null);
    try {
      const { reassigned } = await reassign.mutateAsync({ fromUserId: user.id, toUserId });
      handleClose();
      toast.success(t.moved(String(reassigned)));
    } catch (err: unknown) {
      setError(
        apiErrorCode(err) === "invalid_owner" ? t.invalidOwner : apiErrorMessage(err, t.failed),
      );
    }
  };

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title(name)}</DialogTitle>
          <DialogDescription>{t.description(name)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}
          <div className="space-y-2">
            <Label htmlFor="reassign-new-owner">{t.newOwner}</Label>
            <SearchableSelect
              id="reassign-new-owner"
              value={toUserId}
              onValueChange={setToUserId}
              options={options}
              placeholder={t.choose}
              searchPlaceholder={t.search}
              emptyMessage={t.noUsers}
              noResultsMessage={t.noResults}
              disabled={isPending}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
          <Button onClick={handleMove} disabled={!toUserId} loading={isPending}>
            {t.move}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
